import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage';
import * as XLSX from 'xlsx';
import { ApiService } from '../../../shared/services/api.service';
import { AuthService } from '../../../core/auth/auth.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { environment } from '../../../../environments/environment';

const TRUST_NUMBER_REGEX = /^(?:(?:IT|MT)\d{1,6}|\d{1,6})\/\d{4}(?:\([A-Z]{1,2}\))?$/;

interface UploadBatchEntry {
  rawValue: string;
  normalizedTrust: string | null;
}

@Component({
  selector: 'app-trust-request',
  templateUrl: './trust-request.component.html',
  styleUrls: ['./trust-request.component.css']
})
export class TrustRequestComponent implements OnInit {
  requestForm: FormGroup;
  readonly maxTrustNumbers = 20;
  readonly defaultMaxRecords = 100;
  readonly maxUploadSizeBytes = 1024 * 1024;
  isSubmitting = false;
  selectedTabIndex = 0;
  uploadedFileName = '';
  uploadedFilePath = '';
  parsedUploadEntries: UploadBatchEntry[] = [];
  uploadParseStatusMessage = '';

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private router: Router,
    private authService: AuthService,
    private analyticsService: AnalyticsService
  ) {
    this.requestForm = this.fb.group({
      trustNumbers: this.fb.array([this.fb.control('', this.trustNumberValidator)]),
      maxRecords: [this.defaultMaxRecords, [Validators.required, Validators.min(1), Validators.max(1000)]]
    });
  }

  ngOnInit(): void {}

  get trustNumbers(): FormArray {
    return this.requestForm.get('trustNumbers') as FormArray;
  }

  trustNumberValidator = (control: any): { [key: string]: any } | null => {
    if (!control.value) return null;
    const normalized = this.normalizeTrustNumber(String(control.value));
    return normalized ? null : { invalidTrustNumber: true };
  };

  get hasAnyTrustNumber(): boolean {
    return this.getNonEmptyTrustValues().length > 0;
  }

  addTrustField(): void {
    if (this.trustNumbers.length >= this.maxTrustNumbers) {
      this.snackBar.open(`Maximum of ${this.maxTrustNumbers} trust numbers allowed`, 'Close', { duration: 3000 });
      return;
    }
    this.trustNumbers.push(this.fb.control('', this.trustNumberValidator));
  }

  removeTrustField(index: number): void {
    if (this.trustNumbers.length > 1) {
      this.trustNumbers.removeAt(index);
    }
  }

  onTrustPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    if (!clipboardData) return;
    const text = clipboardData.getData('text') || '';
    if (!text) return;

    event.preventDefault();

    const lines = text.split(/\r?\n/);
    const allTokens: string[] = [];
    for (const line of lines) {
      const tokens = line.split(/\s+/).filter((t: string) => t.trim().length > 0);
      allTokens.push(...tokens);
    }

    const validTrusts: string[] = [];
    for (const token of allTokens) {
      const normalized = this.normalizeTrustNumber(token);
      if (!normalized) continue;
      validTrusts.push(normalized);
      if (validTrusts.length >= this.maxTrustNumbers) break;
    }

    if (!validTrusts.length) {
      this.snackBar.open('No valid trust numbers found in pasted data', 'Close', { duration: 3000 });
      return;
    }

    this.trustNumbers.clear();
    validTrusts.forEach((trustNumber) => {
      this.trustNumbers.push(this.fb.control(trustNumber, this.trustNumberValidator));
    });
  }

  async onSubmit(): Promise<void> {
    const trustValues = this.getNonEmptyTrustValues();
    if (trustValues.length === 0) {
      this.snackBar.open('At least one valid trust number is required', 'Close', { duration: 3000 });
      return;
    }

    if (!this.requestForm.valid) {
      this.snackBar.open('Please fill in all required fields correctly', 'Close', { duration: 3000 });
      return;
    }

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const payloadBase: any = {
      maxRecords: this.defaultMaxRecords
    };

    try {
      if (trustValues.length > 1) {
        await this.submitMultiSearch(trustValues, payloadBase.maxRecords, 'NEW_REQUEST');
        return;
      }

      const trustNumber = trustValues[0];
      const response = await firstValueFrom(
        this.apiService.createTrustRequest({
          ...payloadBase,
          trustNumber,
        })
      );
      this.analyticsService.trackEvent('trust_search_single_created', {
        source: this.isUserRouteContext() ? 'USER_TRUST_REQUEST' : 'ADMIN_TRUST_REQUEST',
      });
      const from = this.isUserRouteContext() ? 'user-trust-request' : 'trust-request';
      const base = this.isUserRouteContext() ? '/user/trust-data' : '/admin/trust-data';
      await this.router.navigate([base, response.requestId], { queryParams: { from } });
    } catch (error: any) {
      const message = this.toCreateRequestErrorMessage(error);
      this.snackBar.open(message, 'Close', { duration: 6000 });
    } finally {
      this.isSubmitting = false;
    }
  }

  async onUploadFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.uploadedFileName = file.name;
    this.uploadedFilePath = '';
    this.parsedUploadEntries = [];
    this.uploadParseStatusMessage = 'Processing file...';

    try {
      this.validateUploadFile(file);
      await this.validateFileSignature(file);
      await this.uploadFileToStorage(file);
      this.parsedUploadEntries = await this.extractUploadEntriesFromFile(file);

      if (this.parsedUploadEntries.length < 1) {
        await this.deleteUploadedFile();
        this.uploadParseStatusMessage = 'No non-empty rows were found. Please upload a different file.';
        this.snackBar.open('Upload must contain at least one non-empty row', 'Close', { duration: 5000 });
        return;
      }

      const validCount = this.parsedUploadEntries.filter((entry) => !!entry.normalizedTrust).length;
      this.analyticsService.trackEvent('upload_trust_numbers_parsed', {
        trust_count: validCount,
        row_count: this.parsedUploadEntries.length,
        file_extension: this.getFileExtension(file.name),
      });
      this.uploadParseStatusMessage = 'File parsed successfully. Click Search to begin.';
    } catch (error: any) {
      await this.deleteUploadedFile();
      this.uploadedFilePath = '';
      this.parsedUploadEntries = [];
      this.uploadParseStatusMessage = 'File parsing failed. Please upload a valid CSV or XLSX file.';
      this.snackBar.open(error?.message || 'Failed to process upload file', 'Close', { duration: 6000 });
    } finally {
      input.value = '';
    }
  }

  async submitUploadedTrusts(): Promise<void> {
    if (this.parsedUploadEntries.length < 1) {
      this.snackBar.open('Upload a file containing at least one non-empty row first', 'Close', { duration: 5000 });
      return;
    }
    if (this.isSubmitting) return;

    const trustValues = this.parsedUploadEntries
      .map((entry) => entry.normalizedTrust)
      .filter((value): value is string => !!value);

    if (!trustValues.length) {
      this.snackBar.open('No valid trust numbers found in the uploaded file', 'Close', { duration: 5000 });
      return;
    }

    this.isSubmitting = true;
    try {
      await this.submitMultiSearch(trustValues, this.defaultMaxRecords, 'CSV_UPLOAD');
    } catch (error: any) {
      this.snackBar.open(
        error?.message || 'An unexpected error occurred while submitting uploaded trust numbers',
        'Close',
        { duration: 5000 }
      );
    } finally {
      await this.deleteUploadedFile();
      this.uploadedFileName = '';
      this.uploadedFilePath = '';
      this.parsedUploadEntries = [];
      this.uploadParseStatusMessage = '';
      this.isSubmitting = false;
    }
  }

  private toCreateRequestErrorMessage(error: any): string {
    const reasonCode = error?.error?.error?.reasonCode;
    if (reasonCode === 'INSUFFICIENT_CREDITS') {
      return 'You have no credits remaining. Please contact an administrator';
    }
    if (reasonCode === 'INSUFFICIENT_USER_CREDITS') {
      return 'You do not have enough credits for this request';
    }
    return error?.error?.error?.message || 'Failed to create trust request';
  }

  private getNonEmptyTrustValues(): string[] {
    return this.trustNumbers.controls
      .map((c) => this.normalizeTrustNumber((c.value || '').toString()))
      .filter((v): v is string => !!v);
  }

  private async submitMultiSearch(
    trustValues: string[],
    maxRecords: number,
    source: 'NEW_REQUEST' | 'CSV_UPLOAD'
  ): Promise<void> {
    const multiResponse = await firstValueFrom(
      this.apiService.createTrustMultiRequest({
        trustNumbers: trustValues,
        maxRecords,
        source,
      })
    );
    this.analyticsService.trackEvent('trust_search_multi_created', {
      source,
      total_inputs: multiResponse.totalInputs,
      submitted_count: multiResponse.submittedCount,
      failed_count: multiResponse.failedCount,
    });
    const targetBase = this.isUserRouteContext() ? '/user/multi-search' : '/admin/multi-search';
    void this.router.navigate([targetBase, multiResponse.requestBatchId]);
  }

  private normalizeTrustNumber(value: string): string | null {
    const compact = value.toUpperCase().replace(/\s+/g, '');
    if (!compact) return null;
    return TRUST_NUMBER_REGEX.test(compact) ? compact : null;
  }

  private validateUploadFile(file: File): void {
    if (file.size > this.maxUploadSizeBytes) {
      throw new Error('File size exceeds 1MB limit');
    }

    const extension = this.getFileExtension(file.name);
    if (!['csv', 'xlsx'].includes(extension)) {
      throw new Error('Only CSV and XLSX files are allowed');
    }

    const csvMimeTypes = ['text/csv', 'application/csv', 'text/plain', 'application/vnd.ms-excel', ''];
    const xlsxMimeTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ''];
    const allowedTypes = extension === 'csv' ? csvMimeTypes : xlsxMimeTypes;
    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type does not match the expected format');
    }
  }

  private async validateFileSignature(file: File): Promise<void> {
    const extension = this.getFileExtension(file.name);
    const headerBytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
    if (headerBytes.length === 0) {
      throw new Error('Uploaded file is empty');
    }

    if (extension === 'xlsx') {
      const isZipHeader = headerBytes[0] === 0x50 && headerBytes[1] === 0x4b;
      if (!isZipHeader) {
        throw new Error('Invalid XLSX file signature');
      }
      return;
    }

    const hasNullByte = headerBytes.some((byte) => byte === 0);
    if (hasNullByte) {
      throw new Error('CSV file appears to be binary and was rejected');
    }
  }

  private async uploadFileToStorage(file: File): Promise<void> {
    const currentUser = await firstValueFrom(this.authService.getCurrentUser());
    if (!currentUser?.uid) {
      throw new Error('Unable to validate authenticated user for file upload');
    }

    const extension = this.getFileExtension(file.name);
    const safeFileName = this.sanitizeFileName(file.name);
    const storagePath = `user_uploads/${environment.tenantKey}/${currentUser.uid}/${Date.now()}-${safeFileName}`;
    const storageRef = ref(getStorage(), storagePath);
    const contentType =
      extension === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
    await uploadBytes(storageRef, file, {
      contentType,
      customMetadata: {
        uploadSource: 'trust-request-file-upload',
      },
    });
    this.uploadedFilePath = storagePath;
  }

  private async deleteUploadedFile(): Promise<void> {
    if (!this.uploadedFilePath) return;
    try {
      const storageRef = ref(getStorage(), this.uploadedFilePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.warn('Failed to delete uploaded file from storage:', error);
    }
  }

  private async extractUploadEntriesFromFile(file: File): Promise<UploadBatchEntry[]> {
    const extension = this.getFileExtension(file.name);
    const rawValues =
      extension === 'xlsx'
        ? await this.extractEntriesFromXlsx(file)
        : await this.extractEntriesFromCsv(file);

    if (rawValues.length > this.maxTrustNumbers) {
      this.snackBar.open(`Only the first ${this.maxTrustNumbers} non-empty rows will be used`, 'Close', {
        duration: 5000,
      });
    }

    return rawValues.slice(0, this.maxTrustNumbers).map((rawValue) => ({
      rawValue,
      normalizedTrust: this.normalizeTrustNumber(rawValue),
    }));
  }

  private async extractEntriesFromCsv(file: File): Promise<string[]> {
    const text = await file.text();
    return this.extractEntriesFromText(text);
  }

  private async extractEntriesFromXlsx(file: File): Promise<string[]> {
    const bytes = await file.arrayBuffer();
    const workbook = XLSX.read(bytes, { type: 'array', raw: false });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) return [];
    const sheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      raw: false,
      blankrows: true,
      defval: '',
    });
    return rows.flatMap((row) =>
      row
        .map((value) => `${value ?? ''}`)
        .filter((value) => value.trim().length > 0)
    );
  }

  private extractEntriesFromText(text: string): string[] {
    const entries: string[] = [];
    const lines = text.split(/\r?\n/);
    for (const line of lines) {
      const tokens = line.split(/[,;\t]/);
      for (const token of tokens) {
        if (token.trim().length > 0) entries.push(token);
      }
    }
    return entries;
  }

  private sanitizeFileName(fileName: string): string {
    const trimmed = fileName.trim().replace(/\s+/g, '_');
    return trimmed.replace(/[^a-zA-Z0-9._-]/g, '');
  }

  private getFileExtension(fileName: string): string {
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  private isUserRouteContext(): boolean {
    return this.router.url.startsWith('/user/');
  }
}
